<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { Icon, Button, Editable, MediaUploaderButton, Textarea, Input } from '@components'
import Prompt from '@components/Prompt.vue'

import { useDeviceLayout } from '../useDeviceLayout'
const $deviceLayout = useDeviceLayout()

import { useCustomizer } from '@store/customizer'
const $customizer = useCustomizer()

import { useSettings } from '@store/settings.js'
const $settings = useSettings()

import { useRoute, useRouter } from 'vue-router'
const route = useRoute()
const router = useRouter()

import { useContent } from './useContent'
const { overviewContent } = useContent()

import moment from 'moment'

const emit = defineEmits(['close', 'submit'])

const props = defineProps({
    pageTitle: {
        type: String,
        default: ''
    },
    next: {
        type: Object
    },
    previous: {
        type: Object
    }
})

// const siteLoad = ref(0)

const saveBusinessProfile = async (type) => {
    await $settings.saveSettings()
console.log('Settings saved')
    // if (type == 'logo') Toast.success('Business logo updated successfully')
    if (type == 'details') Toast.success('Business details updated successfully')
}

// const saveSettings = async () => {
//     await $settings.saveSettings('customizer')
//     console.log('Settings saved')
//     Toast.success('Customizer updated successfully')
// }

// const useEditable = () => {
//     const textarea = ref(null)

//     const autoResize = () => {
//         const el = textarea.value
//         // console.log(el)
//         // el.style.height = 'auto'
//         el.style.height = `${el.scrollHeight}px`
//     }
//     return { textarea, autoResize }
// }

// const { textarea, autoResize } = useEditable()

// Run autoResize on mount
// onMounted(() => {
//     autoResize()
// })

onMounted(() => {
    if (!route.query.tab) {
        router.replace({
            query: {
                ...route.query,
                tab: 'steps',
                background: !route.query.background ? $settings.customizer.sidebar.background_type : route.query.background
            }
        })
    }
})

const formatTime = (time, format) => {
    if ($customizer.getMenuStatusById('time', 'show_time')) {
        return moment(time, format).format('HH:mm')
    } else {
        return moment(time, format).format('hh:mm A')
    }
}

const checkVisibility = (feature) => {
    // bail, if sidebar tab is open
    if (route.query.tab == 'sidebar') return true

    // check if feature is included in route name
    let items = {
        'customizer.form.service': ['service'],
        'customizer.form.staff': ['service', 'staff'],
        'customizer.form.date': ['service', 'staff', 'date'],
        'customizer.form.time': ['service', 'staff', 'date', 'time'],
        'customizer.form.account': ['service', 'staff', 'date', 'time'],
        'customizer.form.overview': ['service', 'staff', 'date', 'time'],
    }
    return items[route.name].includes(feature)
}

let customizerMenu = ref($settings.customizer)

watch(() =>$settings.customizer, (newVal) => {
    customizerMenu.value = $settings.customizer
}, { deep: true })


const findItemPriority = (id) => {
    return Number(customizerMenu.value[id]?.priority);
}

const menus = computed(() => {
    let list = [
        {
            id: 'service',
            title: overviewContent.value.service.name,
            icon: 'star04',
            link: { name: 'customizer.form.service' },
            visibility: checkVisibility('service'),
            priority: findItemPriority('service')
        },
        {
            id: 'staff',
            title: overviewContent.value.staff.display_name,
            icon: 'cube01',
            link: { name: 'customizer.form.staff' },
            visibility: checkVisibility('staff'),
            priority: findItemPriority('staff')
        },
        {
            id: 'date',
            title: overviewContent.value.date.format('DD MMM YYYY'),
            icon: 'calendar',
            link: { name: 'customizer.form.date' },
            visibility: checkVisibility('date'),
            priority: findItemPriority('date')
        },
        {
            id: 'time',
            title: formatTime(overviewContent.value.time, 'HH:mm'),
            icon: 'clock',
            link: { name: 'customizer.form.time' },
            visibility: checkVisibility('time'),
            priority: findItemPriority('time')
        }
    ]
    return list.sort((a, b) => a.priority - b.priority)
})

const backgroundTheme = computed(() => {
    if ($settings.customizer.sidebar.show_background == false) return

    if ($settings.customizer.sidebar.background_type == 'image' && $settings.customizer.sidebar.background_image) return `background-image: url(${$settings.customizer.sidebar.background_image});`
    else return `background-color: ${$settings.customizer.sidebar.background_color};`
})

const logo = computed(() => {
    if ($settings.customizer.sidebar.business_custom_logo !== '') return $settings.customizer.sidebar.business_custom_logo

    return $settings.business.full_logo_url
})

const sidebarPosition = computed(() => {
    switch ($settings.customizer.sidebar.content_position) {
        case 'Left bottom':
            return 'justify-end'
        case 'Left top':
            return ''
        case 'Right bottom':
            return 'justify-end items-end'
        case 'Right top':
            return 'items-end'
    }
})

const isPositionRight = computed(() => {
    return $settings.customizer.sidebar.content_position.includes('Right')
})

const isEditable = computed(() => {
    return route.query.tab == 'sidebar'
})

const handleSidebarInputClick = (e) => {
    router.replace({ query: { ...route.query, tab: 'sidebar' } })
}

const showMore = ref(false)

const truncatedAbout = computed(() => {
    let text = $settings.addons.staffs ? $settings.customizer.sidebar.business_custom_about : $settings.customizer.sidebar.provider_bio
    if (!text) return ''

    if (showMore.value) return text

    // if text exceeds 76 characters
    if (text.length > 76) {
        return text.slice(0, 76) + '...'
    }

    // if text exceeds 3 lines
    if (String(text).split('\n').length > 3) {
        return String(text).split('\n').slice(0, 3).join('\n') + '...'
    }

    return text
})
</script>

<template>
    <div class="max-w-[800px] m-auto">
        <Prompt />
        <!-- header -->
        <div class="flex items-center justify-between">
            <p class="text-[#101828] font-semibold text-xl capitalize">{{ props.pageTitle }}</p>
            <div class="flex gap-2">
                <Icon v-for="device in $deviceLayout.previewDevices" :name="device.icon" size="40px" class="p-2 rounded-md cursor-pointer hover:bg-white hover:text-[#101828] transition-all duration-300 hover:shadow-[0px_1px_2px_0px_#1018280D]" :class="[$deviceLayout.activePreviewDevice == device.name ? 'shadow-[0px_1px_2px_0px_#1018280D] text-[#101828] bg-white' : 'text-[#98A2B3]']" @click="$deviceLayout.changePreviewDevice(device.name)" />
            </div>
        </div>
        
        <!-- device content -->
        <div class="prebook-customizer relative flex my-7 min-h-[520px] shadow-[0px_75px_150px_0px_#34405424] m-auto" :style="`width: ${$deviceLayout.previewWidth}; font-family: ${$settings.customizer.design.font_family} !important;`">
            <!-- arrow -->
            <div v-if="props.previous" @click="router.push({ name: props.previous.name, query: { ...route.query } })" class="cursor-pointer flex items-center hover:bg-[#EAECF0] px-[10px] py-[5px] absolute top-1/2 -translate-y-1/2 left-[-94px] rounded-[50px]">
                <Icon name="arrowNarrowLeft" size="34px" class="text-[#98A2B3]"></Icon>
            </div>
            <div v-if="props.next" @click="router.push({ name: props.next.name, query: { ...route.query } })" class="cursor-pointer flex items-center hover:bg-[#EAECF0] px-[10px] py-[5px] absolute top-1/2 -translate-y-1/2 right-[-94px] rounded-[50px]">
                <Icon name="arrowNarrowRight" size="34px" class="text-[#98A2B3]"></Icon>
            </div>

            <!-- frontend sidebar -->
            <div v-if="$deviceLayout.isDesktop" class="w-[240px] p-5 border border-[#D0D5DD] bg-white bg-center bg-no-repeat bg-cover flex flex-col" :class="{ 'rounded-l-lg border-r-0': $customizer.isRounded && !$settings.isRTL, 'rounded-r-lg border-l-0': $customizer.isRounded && $settings.isRTL, '!border-none': $settings.customizer.sidebar.show_background }, sidebarPosition" :style="backgroundTheme">
                <div class="relative w-[100px] max-h-full mb-10">
                    <img v-if="logo" :src="logo" class="logo-icon w-full max-h-full" />
                    <img v-else :src="Image('prebook-logo-light.svg')" width="56px" height="56px" class="logo-icon " />
                    <!-- <div v-else class="w-[56px] h-[56px] bg-[#D0D5DD] rounded border border-[#B692F6]"></div> -->

                    <div class="bg-[#9E77ED] cursor-pointer absolute bottom-[-20px] right-[-10px] border-[3px] border-[#fff] text-[#fff] w-8 h-8 rounded-full flex items-center justify-center">
                        <MediaUploaderButton :crop="true" :ratio="1 / 1" name="form_custom_logo_url" v-model="$settings.customizer.sidebar.business_custom_logo" @change="saveBusinessProfile('logo')">
                            <Icon name="upload01" size="16px"></Icon>
                        </MediaUploaderButton>
                    </div>
                </div>

                <!-- for business profile -->
                <div v-if="$settings.addons.staffs">
                    <!-- name -->
                    <Input @click="handleSidebarInputClick" v-model="$settings.customizer.sidebar.business_custom_name" type="text" class="mt-1.5" inputMainContainerClass="!bg-transparent !border-none !shadow-none" inputContainerClass="!ring-0 !p-0" :inputClass="`!p-1 !rounded bg-transparent border-none text-lg font-semibold !shadow-none focus:!outline focus:!outline-1 focus:!outline-[#B692F6] ${isEditable ? '!outline !outline-1 !outline-[#B692F6]' : ''} ${isPositionRight ? 'text-end' : ''}`" :inputStyle="`color: ${$settings.customizer.sidebar.business_name_color};`"></Input>

                    <!-- about -->
                    <Textarea v-if="isEditable" @click="handleSidebarInputClick" v-model="$settings.customizer.sidebar.business_custom_about" autoHeight class="mt-2" :textareaClass="`!p-1 !rounded bg-transparent border-none text-sm !shadow-none focus:outline-[#B692F6] ${isEditable ? '!outline !outline-1 !outline-[#B692F6]' : ''} ${isPositionRight ? 'text-end' : ''}`" :textareaStyle="`color: ${$settings.customizer.sidebar.business_about_color};`"></Textarea>
                    <div v-else @click="handleSidebarInputClick" class="whitespace-pre-line p-1 font-normal text-sm" :style="{ color: $settings.customizer.sidebar.business_about_color }">{{ truncatedAbout }} <span v-if="String($settings.customizer.sidebar.business_custom_about).length > 76 || String($settings.customizer.sidebar.business_custom_about).split('\n').length > 3" class="underline cursor-pointer" @click="showMore = !showMore">{{ showMore ? 'show less' : 'show more' }}</span></div>
                </div>

                <!-- for service provider profile -->
                <div v-if="!$settings.addons.staffs">
                    <!-- name -->
                    <Input @click="handleSidebarInputClick" v-model="$settings.customizer.sidebar.provider_display_name" type="text" class="mt-1.5" inputMainContainerClass="!bg-transparent !border-none !shadow-none" inputContainerClass="!ring-0 !p-0" :inputClass="`!p-1 !rounded bg-transparent border-none text-lg font-semibold !shadow-none focus:!outline focus:!outline-1 focus:!outline-[#B692F6] ${isEditable ? '!outline !outline-1 !outline-[#B692F6]' : ''} ${isPositionRight ? 'text-end' : ''}`" :inputStyle="`color: ${$settings.customizer.sidebar.provider_display_name_color};`"></Input>

                    <!-- about -->
                    <Textarea v-if="isEditable" @click="handleSidebarInputClick" v-model="$settings.customizer.sidebar.provider_bio" autoHeight class="mt-2" :textareaClass="`!p-1 !rounded bg-transparent border-none text-sm !shadow-none focus:outline-[#B692F6] ${isEditable ? '!outline !outline-1 !outline-[#B692F6]' : ''} ${isPositionRight ? 'text-end' : ''}`" :textareaStyle="`color: ${$settings.customizer.sidebar.provider_bio_color};`"></Textarea>
                    <div v-else @click="handleSidebarInputClick" class="whitespace-pre-line p-1 font-normal text-sm" :style="{ color: $settings.customizer.sidebar.provider_bio_color }">{{ truncatedAbout }} <span v-if="String($settings.customizer.sidebar.provider_bio).length > 76 || String($settings.customizer.sidebar.provider_bio).split('\n').length > 3" class="underline cursor-pointer" @click="showMore = !showMore">{{ showMore ? 'show less' : 'show more' }}</span></div>
                </div>


                <!-- sidebar menus -->
                <div class="flex flex-col gap-3 mt-10">
                    <template v-for="menu in menus" :key="menu">
                        <div v-if="menu.visibility" class="p-[4px_12px_4px_10px] flex gap-2 items-center rounded-2xl w-fit" :class="[$settings.customizer.sidebar.show_background ? 'bg-white' : 'bg-[#F2F4F7]'], { 'ml-auto': isPositionRight }">
                            <Icon :name="menu.icon" size="12px" class="text-[#667085]"></Icon>
                            <div class="text-[#344054] text-sm font-medium">{{ menu.title }}</div>
                        </div>
                    </template>
                </div>
            </div>

            <!-- frontend content -->
            <div class="relative max-h-[520px] border border-[#D0D5DD] bg-white" :class="[$deviceLayout.isDesktop ? 'w-[calc(100%-240px)]' : 'w-full'], { 'rounded-r-lg': $customizer.isRounded && !$settings.isRTL, '!rounded-l-lg bg-white': $customizer.isRounded && $settings.isRTL, '!rounded-lg': $deviceLayout.activePreviewDevice != 'desktop' && $customizer.isRounded }">
                <div class="p-5 overflow-y-scroll" :class="[$slots.footer ? 'h-[calc(100%-70px)]' : 'h-full']">
                    <slot></slot>
                </div>

                <div class="absolute left-0 bottom-0 w-full bg-white py-3 px-5 border-t" v-if="$slots.footer">
                    <slot name="footer"></slot>
                </div>
            </div>

        </div>

        <!-- footer actions -->
        <div class="flex gap-3 justify-between items-center mt-8">
            <Button @click="$customizer.cancel(true)" size="lg" type="secondary_gray" radius="square">Cancel</Button>
            <Button @click="$customizer.publish" size="md" type="primary" radius="square" class="w-[219px]">Update</Button>
        </div>
    </div>
</template>



<style>

</style>